/**
 * Shared icon catalog for the product nav tabs system.
 *
 * Both the admin configuration UI (`app/(admin)/admin/products/nav-tabs/`)
 * and the public mobile rail (`components/products/nav/ProductQuickNav`) need
 * the same name → lucide icon mapping, so it lives here.
 *
 * Adding a new icon: append an entry to `ICON_CHOICES`. The admin select
 * picks it up automatically; the public rail renders it via `NavIcon`.
 */

import {
  Baby,
  Bell,
  Box,
  Cake,
  CalendarHeart,
  Candy,
  Cherry,
  Circle,
  Cross,
  Crown,
  Droplets,
  Flame,
  Flower,
  Flower2,
  Gem,
  Gift,
  GraduationCap,
  Heart,
  HeartHandshake,
  Home,
  LayoutGrid,
  Leaf,
  Moon,
  Package,
  Palette,
  PartyPopper,
  PoundSterling,
  ShoppingBag,
  Snowflake,
  Sparkles,
  Sprout,
  Star,
  Sun,
  TreePine,
  Trophy,
  Wheat,
  type LucideIcon,
} from "lucide-react";

/**
 * Uniform stroke width for EVERY nav-tab icon, wherever it renders (storefront
 * rail + admin preview + admin picker). Single source of truth so the icons
 * never read as "one thick, one thin" across surfaces. All icons are lucide, so
 * the same numeric stroke gives identical visual weight.
 */
export const NAV_ICON_STROKE = 2.25;

export const ICON_CHOICES: ReadonlyArray<{ name: string; Component: LucideIcon }> = [
  { name: "LayoutGrid", Component: LayoutGrid },
  { name: "Flame", Component: Flame },
  { name: "Flower", Component: Flower },
  { name: "Flower2", Component: Flower2 },
  { name: "Leaf", Component: Leaf },
  { name: "Sprout", Component: Sprout },
  { name: "Wheat", Component: Wheat },
  { name: "HeartHandshake", Component: HeartHandshake },
  { name: "Heart", Component: Heart },
  { name: "Circle", Component: Circle },
  { name: "Star", Component: Star },
  { name: "Sparkles", Component: Sparkles },
  { name: "Trophy", Component: Trophy },
  { name: "Crown", Component: Crown },
  { name: "GraduationCap", Component: GraduationCap },
  { name: "Gift", Component: Gift },
  { name: "Cake", Component: Cake },
  { name: "Candy", Component: Candy },
  { name: "Baby", Component: Baby },
  { name: "Cross", Component: Cross },
  { name: "Bell", Component: Bell },
  { name: "CalendarHeart", Component: CalendarHeart },
  { name: "Gem", Component: Gem },
  { name: "PoundSterling", Component: PoundSterling },
  { name: "Sun", Component: Sun },
  { name: "Moon", Component: Moon },
  { name: "Snowflake", Component: Snowflake },
  { name: "TreePine", Component: TreePine },
  { name: "Cherry", Component: Cherry },
  { name: "Droplets", Component: Droplets },
  { name: "Palette", Component: Palette },
  { name: "ShoppingBag", Component: ShoppingBag },
  { name: "PartyPopper", Component: PartyPopper },
  { name: "Home", Component: Home },
  { name: "Package", Component: Package },
  { name: "Box", Component: Box },
];

/**
 * Heuristic icon picker — given a slug or label, return the most
 * appropriate icon name from `ICON_CHOICES`. Used by the auto-suggestion
 * flow when generating subcategory-based tabs (Bags → ShoppingBag, etc.).
 *
 * Matching uses a leading word-boundary + stem so plurals match too:
 * `\bbag` catches "bag", "bags", "bagged" but not "garbage" (no boundary
 * between "gar" and "bag"). Order matters — earlier rules win, so
 * "gift wrap" resolves to ShoppingBag (wrap is the noun) but "gift kit"
 * resolves to Gift (gift fires first). Falls through to "Package" as a
 * generic "thing" glyph rather than the colour-specific "Circle" default.
 */
export function pickIconForLabel(input: string): string {
  const s = input.toLowerCase();
  if (/\b(bag|tote|pouch|wrap)/.test(s)) return "ShoppingBag";
  if (/\b(balloon|party)/.test(s)) return "PartyPopper";
  if (/\b(gift|extra|present)/.test(s)) return "Gift";
  if (/\b(card|note|message)/.test(s)) return "HeartHandshake";
  if (/\b(candle|wax)/.test(s)) return "Flame";
  if (/\b(ribbon|sparkle|glitter)/.test(s)) return "Sparkles";
  if (/\b(rose|flower|bouquet|petal)/.test(s)) return "Flower";
  if (/\b(leaf|foliage|green)/.test(s)) return "Leaf";
  if (/\b(box|case|kit)/.test(s)) return "Box";
  if (/\b(hot|trending|top)/.test(s)) return "Flame";
  if (/\b(featured|favourite|star)/.test(s)) return "Star";
  return "Package";
}

/**
 * Semantic accent gradient per nav tab, so each category reads as its own
 * thing instead of every tab sharing one sage circle. Keyed by keyword against
 * the tab's slug + label (robust to admin-renamed tabs), earliest match wins.
 *
 * The gradient strings are FULL literal Tailwind classes (not built
 * dynamically) so the JIT compiler emits them. Pair with `bg-linear-to-br`.
 * Meanings: fresh→green (living), crochet→violet (craft), best-sellers→gold
 * (popular), graduation→indigo (academic), gifts→pink, value→teal,
 * accessories→slate (neutral), sympathy→calm blue, etc.
 */
const NAV_TAB_ACCENTS: ReadonlyArray<{ test: RegExp; gradient: string }> = [
  { test: /fresh/, gradient: "from-emerald-400 to-green-600" },
  { test: /crochet/, gradient: "from-violet-400 to-purple-600" },
  { test: /best|seller|\bhot\b|trend/, gradient: "from-amber-400 to-rose-500" },
  { test: /graduat/, gradient: "from-indigo-400 to-blue-700" },
  { test: /creativ/, gradient: "from-fuchsia-400 to-purple-600" },
  { test: /preserved/, gradient: "from-amber-500 to-orange-700" },
  { test: /under|budget|\bvalue\b|£|\bprice|\bsale\b/, gradient: "from-teal-400 to-cyan-600" },
  { test: /accessor|\bbag|tote|pouch/, gradient: "from-slate-400 to-slate-600" },
  { test: /birthday/, gradient: "from-pink-400 to-fuchsia-600" },
  { test: /anniversar|valentine|romance/, gradient: "from-rose-400 to-red-600" },
  { test: /sympath|funeral|condolence/, gradient: "from-slate-400 to-sky-600" },
  { test: /get.?well/, gradient: "from-yellow-400 to-amber-500" },
  { test: /congrat/, gradient: "from-amber-400 to-yellow-500" },
  { test: /thank/, gradient: "from-teal-400 to-emerald-600" },
  { test: /balloon|party/, gradient: "from-sky-400 to-blue-500" },
  { test: /gift|present|extra/, gradient: "from-pink-400 to-rose-500" },
];

/** Brand-sage fallback gradient for tabs that don't match a semantic keyword. */
export const DEFAULT_NAV_ACCENT = "from-sage to-stone";

/** Resolve the `bg-linear-to-br` from/to classes for a tab's icon circle. */
export function getNavTabAccent(slugOrLabel: string): string {
  const s = (slugOrLabel || "").toLowerCase();
  for (const a of NAV_TAB_ACCENTS) if (a.test.test(s)) return a.gradient;
  return DEFAULT_NAV_ACCENT;
}

/** Fast lookup by string name. Unknown names fall back to `Circle`. */
export const ICON_MAP: Map<string, LucideIcon> = new Map(
  ICON_CHOICES.map((c) => [c.name, c.Component])
);

/** Default fallback icon for unknown/missing icon names. */
export const DEFAULT_NAV_ICON: LucideIcon = Circle;

/**
 * Resolve an icon component by name. Centralised so callers don't repeat
 * the `ICON_MAP.get(name) ?? Circle` pattern.
 */
export function getNavIcon(name: string | null | undefined): LucideIcon {
  if (!name) return DEFAULT_NAV_ICON;
  return ICON_MAP.get(name) ?? DEFAULT_NAV_ICON;
}
